import { useMemo } from 'react';
import { useOrderHistory } from '@/hooks/useOrderHistory';

export interface UsualEntry {
  restaurantId: string;
  restaurantName: string;
  restaurantLogo?: string;
  eatzyClientId: string;
  count: number;
  lastOrder: {
    id: string | number;
    type: 'delivery' | 'takeout';
    deliveryLat?: number;
    deliveryLng?: number;
    deliveryAddressId?: number;
  };
}

const COMPLETED_STATUSES = ['completed', 'closed', 'delivered'];

export function useUsuals(limit = 6): { usuals: UsualEntry[]; isLoading: boolean } {
  const { data: ordersResponse, isLoading } = useOrderHistory(1, '', { enabled: true });

  const usuals = useMemo((): UsualEntry[] => {
    const results = ordersResponse?.results ?? [];
    const map: Record<string, UsualEntry> = {};
    for (const o of results) {
      const statusLower = (o.status ?? '').toLowerCase();
      if (!COMPLETED_STATUSES.includes(statusLower)) continue;
      const eatzyClientId = o.restaurant?.profile ?? '';
      const rid = eatzyClientId || String(o.restaurant?.id ?? o.id);
      if (!rid) continue;
      if (!map[rid]) {
        map[rid] = {
          restaurantId: rid,
          restaurantName: o.restaurant?.name ?? 'Unknown',
          restaurantLogo: o.restaurant?.logo,
          eatzyClientId,
          count: 0,
          lastOrder: {
            id: o.id,
            type: (o.type ?? 'takeout') as 'delivery' | 'takeout',
            deliveryLat: o.address?.latitude ? Number(o.address.latitude) : undefined,
            deliveryLng: o.address?.longitude ? Number(o.address.longitude) : undefined,
            deliveryAddressId: o.address?.id ? Number(o.address.id) : undefined,
          },
        };
      }
      map[rid].count++;
    }
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, limit);
  }, [ordersResponse, limit]);

  return { usuals, isLoading };
}
